import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateAdditionalDiscountRequestSchema,
  type CreateAdditionalDiscountRequest,
  type AdditionalDiscountDetail,
  type AdditionalDiscountListItem,
  UpdateAdditionalDiscountRequestSchema,
  type UpdateAdditionalDiscountRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCreateAdditionalDiscount,
  useUpdateAdditionalDiscount,
} from '../use-additional-discounts';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface AdditionalDiscountFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  additionalDiscount?: AdditionalDiscountDetail | AdditionalDiscountListItem | null;
}

const CREATE_DEFAULTS: CreateAdditionalDiscountRequest = {
  code: '',
  name: '',
  amountPaise: '0',
  sortOrder: 0,
  isActive: true,
};

const paiseToRupees = (paise: string | undefined): number | '' => {
  const n = Number(paise ?? '0');
  if (!Number.isFinite(n) || n === 0) return '';
  return n / 100;
};
const rupeesToPaise = (rupees: number | null): string =>
  rupees === null ? '0' : String(Math.round(rupees * 100));

const AdditionalDiscountFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Priority check-in upsell"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="amountPaise">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Upsell amount (Rs)"
          precision={2}
          min={0}
          leftAdornment={<span className="text-sm text-zinc-500">₹</span>}
          value={paiseToRupees(field.value as string | undefined)}
          onValueChange={(v) => field.onChange(rupeesToPaise(v))}
          error={error}
          required
          disabled={pending}
          hint="Fixed rupee amount added to the grand total on the Pricing step."
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive rows are hidden from the Pricing step dropdown."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const AdditionalDiscountFormDrawer = ({
  open,
  onOpenChange,
  mode,
  additionalDiscount,
}: AdditionalDiscountFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateAdditionalDiscount();
  const updateMut = useUpdateAdditionalDiscount();
  const pending = createMut.isPending || updateMut.isPending;

  const createForm = useForm<CreateAdditionalDiscountRequest>({
    resolver: zodResolver(CreateAdditionalDiscountRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateAdditionalDiscountRequest>({
    resolver: zodResolver(UpdateAdditionalDiscountRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (additionalDiscount) {
      updateForm.reset({
        name: additionalDiscount.name,
        amountPaise: additionalDiscount.amountPaise,
        sortOrder: additionalDiscount.sortOrder,
        isActive: additionalDiscount.isActive,
      });
    }
  }, [open, isCreate, additionalDiscount, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createMut.mutateAsync({
      ...values,
      code: deriveCode(values.name),
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!additionalDiscount) return;
    await updateMut.mutateAsync({ id: additionalDiscount.id, payload: values });
    onOpenChange(false);
  });

  const fields = <AdditionalDiscountFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add upsell' : `Edit ${additionalDiscount?.name ?? 'upsell'}`}
      description="The name is what agents see. The amount is a fixed rupee value added to the grand total (cannot be combined with Discount)."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
